import {useT} from '@eitje/i18n'
import {
	DatePickerProvider,
	DatePickerControl,
	DatePickerDayView,
	DatePickerMonthView,
	DatePickerPickerRoot,
	DatePickerRoot,
	DatePickerTrigger,
	DatePickerYearView,
} from '~/components/date_based_pickers/components'
import {date} from '@eitje/utils'
import {EitjeButton} from '~/components/eitje_button'
import {Text} from '~/components/text'

/**
 * @typedef {Object} DatePickerProps
 * @property {string | import('moment').Moment | null} [value]
 * Controlled selected date. A string is normalized to the single-date array expected by Ark UI; null or an empty string clears the picker.
 * @property {(value: string) => void} onChange
 * Called with the selected date formatted as `YYYY-MM-DD`, or an empty string when the selection is cleared.
 * @property {string | import('moment').Moment} [defaultPickerValue] Initial date focused when the picker opens.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').SingleDatePickerContextValues) => import('react').ReactNode} [renderTrigger]
 * Renders a custom trigger with the adapted date-picker context. The default trigger is a button displaying the selected date.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').SingleDatePickerContextValues) => import('react').ReactNode} [renderExtraFooter]
 * Renders additional content below the picker views with the adapted date-picker context.
 * @property {string | import('moment').Moment} [minDate] Earliest selectable date, inclusively.
 * @property {string | import('moment').Moment} [maxDate] Latest selectable date, inclusively.
 * @property {boolean} [disabled] Prevents interaction with the picker.
 * @property {boolean} [readOnly] Prevents value changes while retaining the selected date.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {string} [placeholder] Placeholder shown by the default trigger when no date is selected.
 * @property {boolean} [showTodayFooter] Shows `Today` footer option
 * @property {(date: import('moment').Moment) => boolean} [disabledDateFn] Predicate for whether a date should be disabled
 */

/**
 * @param {DatePickerProps} props
 */
export const DatePicker = ({
	value,
	defaultPickerValue,
	minDate,
	maxDate,
	showTodayFooter,
	disabled,
	readOnly,
	placeholder,
	onOpenChange,
	renderTrigger,
	renderExtraFooter,
	onChange,
	disabledDateFn,
}) => {
	const {t} = useT()

	// TODO(jimmy): Remove this extra conversion of change event value
	// It's still needed due to existing form consumers are expecting a date string instead of Moment for single date picker
	const handleValueChange = values => {
		if (values[0]) {
			onChange(values[0].format())
		} else {
			onChange('')
		}
	}

	let triggerElement = (
		<DatePickerProvider>
			{ctx => (
				<DatePickerTrigger>
					<EitjeButton dropdown>
						<Text>{ctx.value ? ctx.value.ftime('dayMonthYear') : placeholder || t('select')}</Text>
					</EitjeButton>
				</DatePickerTrigger>
			)}
		</DatePickerProvider>
	)
	if (renderTrigger)
		triggerElement = <DatePickerProvider>{ctx => <DatePickerTrigger>{renderTrigger(ctx)}</DatePickerTrigger>}</DatePickerProvider>

	const todayDate = date()
	// Normalize the public string-or-Moment API to the Moment-only DatePickerRoot boundary.
	const valueOverride = value ? [date(value)] : []
	// Convert ambigous props from (String | Moment) to Moment
	const defaultFocusedValueOverride = defaultPickerValue ? date(defaultPickerValue) : undefined
	const minDateOverride = minDate ? date(minDate) : undefined
	const maxDateOverride = maxDate ? date(maxDate) : undefined
	const isTodaySelectable =
		(!minDateOverride || todayDate.isSameOrAfter(minDateOverride, 'day')) &&
		(!maxDateOverride || todayDate.isSameOrBefore(maxDateOverride, 'day')) &&
		!disabledDateFn?.(todayDate)

	return (
		<DatePickerRoot
			value={valueOverride}
			defaultFocusedValue={defaultFocusedValueOverride}
			onChange={handleValueChange}
			mode="single"
			minDate={minDateOverride}
			maxDate={maxDateOverride}
			disabled={disabled}
			onOpenChange={onOpenChange}
			readOnly={readOnly}
			disabledDateFn={disabledDateFn}
		>
			<DatePickerControl>{triggerElement}</DatePickerControl>
			<DatePickerPickerRoot renderExtraFooter={renderExtraFooter} showTodayFooter={isTodaySelectable && showTodayFooter}>
				<DatePickerDayView />
				<DatePickerMonthView />
				<DatePickerYearView />
			</DatePickerPickerRoot>
		</DatePickerRoot>
	)
}
